import type { Preview } from '@/api/models';
import { cn } from '@/lib/cn';

/**
 * The pre-staging preview: a link and a state, never a build log.
 *
 * Raw build output routinely contains vendor strings, file paths and tokens, so
 * it is an expert surface. A client gets the address of their site and an
 * honest word about whether it is currently showing their latest change.
 *
 * It is one mono line under the title rather than the block it used to be. The
 * block spent four lines and a lamp saying what two words say, and on a surface
 * whose whole argument is calm, the preview is a fact, not an announcement.
 *
 * `stale` is the state worth getting right: the site is up and reachable, but
 * it is showing the change *before* the one currently being made. Saying
 * "ready" there would send someone to look at the wrong thing and conclude the
 * product does not work.
 */

type PreviewState = NonNullable<NonNullable<Preview>['state']>;

const STATE_COPY: Record<PreviewState, { word: string; tone: 'fresh' | 'moving' | 'fault' }> = {
  none: { word: 'not published yet', tone: 'fresh' },
  building: { word: 'updating', tone: 'moving' },
  ready: { word: 'up to date', tone: 'fresh' },
  failed: { word: 'did not update', tone: 'fault' },
  stale: { word: 'behind your latest change', tone: 'moving' },
};

export function PreviewLink({ preview, className }: { preview: Preview | null | undefined; className?: string }) {
  const state = preview?.state ?? 'none';
  const copy = STATE_COPY[state] ?? STATE_COPY.none;
  const url = preview?.url ?? null;

  const word = (
    <span
      className={cn(
        copy.tone === 'fresh' && 'text-graphite-300',
        copy.tone === 'moving' && 'text-signal',
        copy.tone === 'fault' && 'text-alarm',
      )}
    >
      {copy.word}
    </span>
  );

  if (!url) {
    return (
      <p className={cn('mt-2.5 flex min-w-0 items-center gap-2 font-mono text-[0.71875rem] text-graphite-500', className)}>
        <span className="text-graphite-300">Preview</span>
        {word}
      </p>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'mt-2.5 inline-flex min-w-0 max-w-full items-center gap-2 font-mono text-[0.71875rem] text-graphite-500',
        className,
      )}
    >
      <span className="truncate underline decoration-rule-strong underline-offset-[3px]">
        {url.replace(/^https?:\/\//, '')}
      </span>
      {word}
    </a>
  );
}
